import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer, GraduationCap, FileText } from "lucide-react";
import api from "../lib/api";
import { Spinner, Pill } from "../components/Primitives";
import BookmarkButton from "../components/BookmarkButton";
import ShareButton from "../components/ShareButton";
import usePageMeta from "../hooks/usePageMeta";
import AttachedFiles from "../components/AttachedFiles";
import { Paywall } from "../components/Premium";

// Minimal markdown: ## headings, **bold**, - lists, 1. ordered lists, paragraphs.
function renderMarkdown(md) {
  const blocks = md.split("\n\n");
  return blocks.map((block, i) => {
    if (block.startsWith("## ")) {
      return <h2 key={i}>{block.replace("## ", "")}</h2>;
    }
    const lines = block.split("\n");
    const isList = lines.every((l) => l.trim().startsWith("- "));
    const isOrdered = lines.every((l) => /^\d+\.\s/.test(l.trim()));
    const fmt = (t) => {
      const parts = t.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((p, j) =>
        p.startsWith("**") ? <strong key={j}>{p.replace(/\*\*/g, "")}</strong> : <span key={j}>{p}</span>
      );
    };
    if (isList) {
      return <ul key={i}>{lines.map((l, j) => <li key={j}>{fmt(l.replace(/^- /, ""))}</li>)}</ul>;
    }
    if (isOrdered) {
      return <ol key={i}>{lines.map((l, j) => <li key={j}>{fmt(l.replace(/^\d+\.\s/, ""))}</li>)}</ol>;
    }
    return <p key={i}>{fmt(block)}</p>;
  });
}

export default function LessonDetail() {
  const { slug } = useParams();
  const [l, setL] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    setLoading(true);
    setDenied(false);
    api.get(`/lessons/${slug}`)
      .then((r) => setL(r.data))
      .catch((e) => {
        setL(null);
        setDenied([401, 402].includes(e?.response?.status));
      })
      .finally(() => setLoading(false));
  }, [slug]);

  usePageMeta(
    l ? { title: l.title, description: l.subtitle, image: l.image_url } : { title: "Lessons" }
  );

  if (loading) return <div className="min-h-[60vh]"><Spinner /></div>;
  if (!l && denied) return <Paywall />;
  if (!l) return <div className="mx-auto max-w-7xl px-6 py-32 text-center text-muted">Lesson not found.</div>;

  if (l.locked) {
    return (
      <div data-testid="lesson-teaser-page">
        <div className="relative h-[40vh] min-h-[280px] overflow-hidden">
          <img src={l.image_url} alt={l.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/75 to-ink/30" />
          <div className="absolute inset-x-0 bottom-0">
            <div className="mx-auto max-w-3xl px-6 pb-10">
              <Link to="/lessons" className="mb-4 inline-flex items-center gap-2 text-sm text-gold hover:underline" data-testid="back-to-lessons">
                <ArrowLeft className="h-4 w-4" /> All Lessons
              </Link>
              <Pill tone="gold">{l.category}</Pill>
              <h1 className="mt-3 font-serif text-4xl font-semibold text-bone lg:text-5xl">{l.title}</h1>
            </div>
          </div>
        </div>
        <article className="mx-auto max-w-3xl px-6 py-12">
          <p className="border-b border-edge pb-6 font-serif text-xl text-[#cfc9bd]">{l.subtitle}</p>
          <div className="relative mt-8">
            <p className="text-lg leading-relaxed text-bone/90" data-testid="lesson-teaser-text">{l.teaser}</p>
            <div className="pointer-events-none absolute inset-x-0 -bottom-1 h-24 bg-gradient-to-t from-ink to-transparent" />
          </div>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-gold" data-testid="lesson-teaser-note">
            Continue reading with Premium —
          </p>
          <div className="mt-6">
            <Paywall compact />
          </div>
        </article>
      </div>
    );
  }

  const classroomUrl = `https://classroom.google.com/share?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(l.title)}&body=${encodeURIComponent(l.subtitle)}`;

  return (
    <div data-testid="lesson-detail-page" id="lesson-printable">
      <div className="no-print relative h-[40vh] min-h-[280px] overflow-hidden">
        <img src={l.image_url} alt={l.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/75 to-ink/30" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-3xl px-6 pb-10">
            <Link to="/lessons" className="mb-4 inline-flex items-center gap-2 text-sm text-gold hover:underline" data-testid="back-to-lessons">
              <ArrowLeft className="h-4 w-4" /> All Lessons
            </Link>
            <Pill tone="gold">{l.category}</Pill>
            <h1 className="mt-3 font-serif text-4xl font-semibold text-bone lg:text-5xl">{l.title}</h1>
          </div>
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-6 py-12">
        {/* Print-only header */}
        <div className="print-header hidden">
          <h1 className="font-serif text-4xl font-semibold">{l.title}</h1>
          <p className="mt-1 text-sm uppercase tracking-wide">{l.category} · {l.reading_level} · Our African Narratives</p>
        </div>

        <div className="flex flex-col gap-4 border-b border-edge pb-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-serif text-xl text-[#cfc9bd]">{l.subtitle}</p>
          <div className="no-print flex flex-wrap items-center gap-2">
            <Link
              to={`/lessons/${l.slug}/worksheet`}
              data-testid="worksheet-btn"
              className="inline-flex items-center gap-2 border border-gold bg-gold/10 px-3 py-2 text-xs font-medium text-gold transition-colors hover:bg-gold hover:text-ink"
            >
              <FileText className="h-4 w-4" /> Student Worksheet
            </Link>
            <a
              href={classroomUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="classroom-share-btn"
              className="inline-flex items-center gap-2 border border-gold bg-gold/10 px-3 py-2 text-xs font-medium text-gold transition-colors hover:bg-gold hover:text-ink"
            >
              <GraduationCap className="h-4 w-4" /> Assign to Classroom
            </a>
            <button
              onClick={() => window.print()}
              data-testid="print-lesson-btn"
              className="inline-flex items-center gap-2 border border-edge px-3 py-2 text-xs text-muted transition-colors hover:border-gold hover:text-gold"
            >
              <Printer className="h-4 w-4" /> Print / PDF
            </button>
            <BookmarkButton itemType="lesson" itemId={l.slug} title={l.title} />
            <ShareButton title={l.title} text={`${l.title} — ${l.subtitle}`} />
          </div>
        </div>
        <div className="prose-subira mt-8">{renderMarkdown(l.body)}</div>
        <AttachedFiles attachType="lesson" slug={l.slug} />
        <div className="print-footer hidden">
          <p className="mt-8 border-t pt-3 text-xs">Source: Our African Narratives — Un-erased Black History · {window.location.origin}</p>
        </div>
      </article>
    </div>
  );
}
